import { useCallback } from 'react';
import { useGetUser } from '@/queries/user';
import { useAPI } from '@/services/index';
import {
  getEncryptorStorageData,
  removeEncryptorStorageData,
  setEncryptorStorageData,
} from '../utils/storage';
import {
  convertAESKeyToString,
  encryptByRSA,
  generateRandomAESKey,
} from '../utils';
import { removeFromIndexedDB } from '../db';

export const usePrepareAESKey = () => {
  const { refetch } = useAPI(['sm', 'getPublicKey'], {
    reactQueryOptions: {
      enabled: false,
    },
  });

  const { data: user } = useGetUser({
    enabled: false,
  });

  const { mutateAsync: exchange } = useAPI(['sm', 'exchangeEncryptedKey'], {
    isMutation: true,
  });

  const prepareAESKey = useCallback(
    async ({
      shouldRemoveCurrent = false,
    }: {
      shouldRemoveCurrent?: boolean;
    } = {}) => {
      if (shouldRemoveCurrent) {
        removeEncryptorStorageData();
      }
      // Remove old db
      removeFromIndexedDB('key');

      const { value, id } = getEncryptorStorageData() || {};

      const isKeyBelongToCurrentUser = user && user.pk === id;

      if (!value || !isKeyBelongToCurrentUser) {
        const { data } = await refetch();

        const publicKey = data?.publicKey;

        if (publicKey) {
          const generatedAESKey = await generateRandomAESKey();

          const stringedAESKey = await convertAESKeyToString(generatedAESKey);

          setEncryptorStorageData({
            value: stringedAESKey,
            id: user?.pk,
          });

          const encryptedAES = await encryptByRSA(
            data.publicKey,
            stringedAESKey
          );

          await exchange({ key: encryptedAES });
        } else {
          throw new Error('Failed to get AES Key');
        }
      }
    },
    []
  );

  return { prepareAESKey };
};
